import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import FloatingHelpButton from '../components/landing/FloatingHelpButton';
import ChatWidget from '../components/ChatWidget/ChatWidget';
import './LegalPage.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <Header />

      <div className="legal-hero">
        <div className="container">
          <h1>Terms of Service</h1>
          <p>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      <div className="legal-content">
        <div className="container">
          <section className="legal-section">
            <h2>Agreement to Terms</h2>
            <p>
              By accessing or using eHaazri's attendance management platform ("Service"), you agree to be
              bound by these Terms of Service ("Terms"). If you disagree with any part of these terms,
              you may not access the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>Description of Service</h2>
            <p>
              eHaazri provides a cloud-based attendance management platform that includes:
            </p>
            <ul>
              <li>Biometric facial recognition for attendance verification</li>
              <li>Geofencing and GPS-based location tracking</li>
              <li>Real-time attendance monitoring and reporting</li>
              <li>Leave management and shift scheduling</li>
              <li>Multi-device support (web and mobile applications)</li>
              <li>Administrative dashboard and analytics</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Account Registration</h2>
            <p>
              To use our Service, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Immediately notify us of any unauthorized access or security breach</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>User Responsibilities</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
              <li>Attempt to manipulate or circumvent attendance verification systems</li>
              <li>Share your account credentials with unauthorized persons</li>
              <li>Upload malicious code or attempt to compromise system security</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Interfere with or disrupt the Service or its servers</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Biometric Data and Consent</h2>
            <p>
              By using our facial recognition features, you explicitly consent to:
            </p>
            <ul>
              <li>Collection and processing of your biometric facial data</li>
              <li>Storage of biometric templates in encrypted format</li>
              <li>Use of facial recognition for attendance verification purposes</li>
              <li>Retention of biometric data during your active employment</li>
            </ul>
            <p>
              You have the right to withdraw this consent at any time, though this may limit your ability
              to use certain features of the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>Subscription and Payment</h2>
            <p>
              Our Service operates on a subscription basis:
            </p>
            <ul>
              <li>Subscription fees are billed in advance on a monthly or annual basis</li>
              <li>Payment is due upon subscription renewal unless cancelled beforehand</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>We reserve the right to modify pricing with 30 days' notice</li>
              <li>Failure to pay may result in suspension or termination of service</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Free Trial</h2>
            <p>
              We may offer a free trial period for new users. The trial includes:
            </p>
            <ul>
              <li>Full access to platform features for the specified trial period</li>
              <li>No credit card required for trial activation</li>
              <li>Automatic expiration at the end of the trial period</li>
              <li>Option to subscribe before trial expiration to retain data</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Data Ownership and Usage</h2>
            <p>
              You retain all rights to your data. However, you grant us:
            </p>
            <ul>
              <li>License to store, process, and display your data as necessary to provide the Service</li>
              <li>Right to use anonymized, aggregated data for service improvement and analytics</li>
              <li>Permission to backup your data for disaster recovery purposes</li>
            </ul>
            <p>
              We do not sell, rent, or share your personal data with third parties for marketing purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>Service Availability</h2>
            <p>
              We strive to provide reliable service but cannot guarantee:
            </p>
            <ul>
              <li>Uninterrupted or error-free operation</li>
              <li>Zero downtime during maintenance or updates</li>
              <li>Compatibility with all devices and browsers</li>
              <li>Availability in all geographic regions</li>
            </ul>
            <p>
              We will provide reasonable notice of scheduled maintenance when possible.
            </p>
          </section>

          <section className="legal-section">
            <h2>Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are owned by eHaazri and
              are protected by international copyright, trademark, patent, trade secret, and other
              intellectual property laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>Termination</h2>
            <p>
              We may terminate or suspend your account immediately, without prior notice or liability:
            </p>
            <ul>
              <li>For violation of these Terms</li>
              <li>For fraudulent or illegal activity</li>
              <li>For non-payment of subscription fees</li>
              <li>At our discretion for any other reason</li>
            </ul>
            <p>
              Upon termination, your right to use the Service will immediately cease, and your data may
              be deleted after a grace period.
            </p>
          </section>

          <section className="legal-section">
            <h2>Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, eHaazri shall not be liable for:
            </p>
            <ul>
              <li>Indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Service interruptions or data loss</li>
              <li>Third-party actions or content</li>
            </ul>
            <p>
              Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.
            </p>
          </section>

          <section className="legal-section">
            <h2>Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless eHaazri and its affiliates from any claims, damages,
              losses, or expenses arising from:
            </p>
            <ul>
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your violation of applicable laws or regulations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Disclaimer of Warranties</h2>
            <p>
              The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either
              express or implied, including but not limited to implied warranties of merchantability,
              fitness for a particular purpose, or non-infringement.
            </p>
          </section>

          <section className="legal-section">
            <h2>Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction],
              without regard to its conflict of law provisions. Any disputes arising from these Terms or
              the Service shall be resolved in the courts of [Your Jurisdiction].
            </p>
          </section>

          <section className="legal-section">
            <h2>Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of material
              changes via email or through the Service. Continued use of the Service after changes
              constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <ul>
              <li>Email: legal@ehaazri.com</li>
              <li>Website: Use our Contact Support form</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision
              shall be limited or eliminated to the minimum extent necessary, and the remaining provisions
              shall remain in full force and effect.
            </p>
          </section>
        </div>
      </div>

      <Footer />
      <FloatingHelpButton />
      <ChatWidget />
    </div>
  );
};

export default TermsOfService;
